import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  IonContent,
  IonIcon,
  IonText,
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonPage,
  useIonViewWillEnter,
  useIonViewDidLeave,
  SegmentChangeEventDetail,
  useIonViewWillLeave,
} from '@ionic/react'
import { chevronForwardOutline } from 'ionicons/icons'
import { TradeHistory } from './TradeHistory'
import { DetailContent, Chip, CustomSpacer, StyleIonHeader } from '../../../components'
import { RouteComponentProps } from 'react-router'
import { TradeOrderbook } from './TradeOrderbook'
import { StoresContext } from '../../../provider/StoreProvider'
import { observer } from 'mobx-react'
import { isLoaded, LOADING_STATUS, amountString, ResWebSocket, OrderPageSectors } from '../../../common'

interface IonSegmentCustomEvent<T> extends CustomEvent<T> {
  detail: T
  target: HTMLIonSegmentElement
}

type Props = RouteComponentProps<{
  productId: string
  sector?: OrderPageSectors
}>

export const Trade: React.FC<Props> = observer(({ match, history }) => {
  const { marketStore, commonStore } = useContext(StoresContext)
  const [listType, setListType] = useState<OrderPageSectors>(match.params.sector ?? 'TRADE')
  const [orderbookData, setOrderbookData] = useState<ResWebSocket | LOADING_STATUS>(LOADING_STATUS.LOADING)
  const loadParam = { productId: Number(match.params.productId) }
  const detailStore = marketStore.productDetailStore
  /* 작품 가격 변동율 계산 */
  const saleCost = Number(isLoaded(detailStore) && detailStore.content?.saleCost) // 판매 시작가
  const currentCost = Number(isLoaded(orderbookData) && orderbookData.currentCost) // 현재가
  const costChangeRate = ((currentCost - saleCost) / saleCost) * 100 // 수익률

  useIonViewWillLeave(() => {
    // 페이지 나갔을 때 탭 초기화
    setListType('TRADE')
  })

  useIonViewWillEnter(() => {
    marketStore.getProductDetail(Number(match.params.productId))
    detailStore.load({ param: loadParam })
    getWebSocket()
  }, [match.params.productId])

  // 트레이드 탭 표시 - 마켓 페이지 진입시 탭 변경을 위한 스토어 값 저장
  useEffect(() => {
    if (detailStore.content?.status === 'SALE' && commonStore.listTab === 'TRADE') {
      commonStore.setListTab('SALE')
    } else if (detailStore.content?.status === 'TRADE') {
      commonStore.setListTab('TRADE')
    } else if (detailStore.content?.status === 'TRADE_STATUS') {
      commonStore.setListTab('TRADE_STATUS')
    } else {
      commonStore.setListTab('SALE')
    }
  }, [detailStore.content?.status, commonStore.listTab])

  /* 웹소켓 구현부 - 시작 */
  let websocket: WebSocket
  let isDisplaying = true

  useIonViewWillEnter(() => {
    isDisplaying = true
    commonStore.setShowTab(false)
  })

  useIonViewDidLeave(() => {
    isDisplaying = false
    websocket?.close()
  })

  const onSegmentChange = (e: IonSegmentCustomEvent<SegmentChangeEventDetail>) => {
    setListType(e.detail.value === 'TRADE' ? 'TRADE' : 'HISTORY')
  }

  const wsLoopPing = async () => {
    do {
      websocket.send(JSON.stringify({ event: 'ping' }))
      await new Promise((f) => setTimeout(f, 30000))
    } while (isDisplaying)
  }

  const getWebSocket = () => {
    websocket = new WebSocket(String(process.env.REACT_APP_STO_WEBSOCKET))
    websocket.onopen = () => {
      websocket.send(JSON.stringify({ event: 'subscribeTrade', data: match.params.productId }))
      wsLoopPing()
    }
    websocket.onclose = (error) => {
      console.log('onClose : ' + error)
      if (isDisplaying) {
        getWebSocket()
      }
    }
    websocket.onerror = (error) => {
      console.log('onError ' + error)
    }
    websocket.onmessage = (e) => {
      try {
        const parse = JSON.parse(e.data)
        if (parse.event !== 'update') {
          return
        }
        setOrderbookData(parse.payload)
      } catch (err: unknown) {
        console.log(err)
        setOrderbookData(LOADING_STATUS.FAILED)
      }
    }
  }
  /* 웹소켓 구현부 - 끝 */

  return (
    <IonPage>
      <StyleIonHeader
        title={detailStore.content ? detailStore.content?.name : '작품 소개'}
        type={'SUB'}
        href={'/market'}
      />
      <IonContent>
        <DetailContent detailStore={detailStore}>
          <CustomSpacer spacing="24px" />
          <PageContainer>
            <TradeHeaderContainer>
              <ProductInfoContainer>
                <IonText>
                  <img
                    src={detailStore.content?.imageUri}
                    alt={detailStore.content?.name}
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null
                      currentTarget.src = '/img/thumbnail.svg'
                    }}
                  />
                </IonText>
                <IonText className="product-info">
                  <IonText className="artist">{detailStore.content?.artist || '-'}</IonText>
                  <IonText className="title">{detailStore.content?.name || '-'}</IonText>
                </IonText>
              </ProductInfoContainer>
              <DetailInfoContainer>
                <span className="detail-info-btn">
                  <IonText onClick={() => history.push(`/market/${match.params.productId}`)}>작품 상세보기</IonText>
                  <IonIcon icon={chevronForwardOutline} />
                </span>
              </DetailInfoContainer>
              <CustomSpacer spacing="23px"></CustomSpacer>
              <TradeInfoBoldContainer>
                <>
                  <IonText>현재가</IonText>
                  <div className="current-coast">
                    <Chip type={costChangeRate > 0 ? 'red' : costChangeRate < 0 ? 'blue' : 'gray'}>
                      {costChangeRate > 0 ? '+' : ''}
                      {amountString(String(costChangeRate), false, 2)}%
                    </Chip>
                    <IonText className="cost">
                      {(isLoaded(orderbookData) && orderbookData.currentCost?.toLocaleString()) || 0}원
                    </IonText>
                  </div>
                </>
              </TradeInfoBoldContainer>
              <CustomSpacer spacing="15px"></CustomSpacer>
              <TradeInfoContainer>
                <IonText>누적 거래액</IonText>
                <IonText>
                  {(isLoaded(orderbookData) && orderbookData.totalTradeBalance?.toLocaleString()) || 0}원
                </IonText>
              </TradeInfoContainer>
              <CustomSpacer spacing="15px"></CustomSpacer>
            </TradeHeaderContainer>
          </PageContainer>
          <IonSegment color="dark" value={listType} onIonChange={(e) => onSegmentChange(e)}>
            <IonSegmentButton value="TRADE">
              <IonLabel>호가</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="HISTORY">
              <IonLabel>주문내역</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </DetailContent>
        {listType !== 'HISTORY' && <TradeOrderbook payload={orderbookData} detailStore={detailStore}></TradeOrderbook>}
        {listType === 'HISTORY' && <TradeHistory productId={Number(match.params.productId)} />}
      </IonContent>
    </IonPage>
  )
})

const PageContainer = styled.div`
  padding: 0 20px;
`

const TradeHeaderContainer = styled.section`
  display: flex;
  flex-direction: column;
`

const ProductInfoContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 0px;
  img {
    border-radius: 7px;
    min-width: 52px;
    width: 100%;
    aspect-ratio: 1/1;
    object-fit: cover;
  }
  .product-info {
    grid-area: 1 / 2 / 2 / 6;
    display: flex;
    flex-direction: column;
    font-size: 15px;
    .artist {
      color: #888888;
      font-size: 13px;
    }
  }
`

const DetailInfoContainer = styled.div`
  width: 100%;
  font-size: 13px;
  .detail-info-btn {
    float: right;
    padding: 6px 0 6px 6px;
    display: flex;
    justify-content: end;
    align-items: center;
    color: #888888;
    :hover {
      cursor: pointer;
      background-color: #f4f4f4;
      border-radius: 7px;
    }
  }
`

const TradeInfoBoldContainer = styled.div`
  font-size: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  .current-coast {
    font-size: 20px;
    font-weight: 900;
    display: flex;
    align-items: center;
    .cost {
      margin-left: 8px;
    }
  }
`
const TradeInfoContainer = styled.div`
  font-size: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`
